import { useEffect, useCallback } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Alert, Button, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

/**
 * Interval (ms) between SW update checks — 10 minutes.
 * Cloudflare Pages deploys are instant; frequent checks ensure
 * users see the prompt quickly after a deploy.
 */
const SW_CHECK_INTERVAL = 10 * 60 * 1000;

/**
 * Delay (ms) before the initial version check on app load.
 * Small enough to catch stale SW caches quickly, large enough
 * to avoid racing with the SW registration on first paint.
 */
const INITIAL_CHECK_DELAY_MS = 3000;

/** Fetch timeout — avoids hanging indefinitely on slow or mid-flight deploys. */
const VERSION_CHECK_TIMEOUT_MS = 5000;

/** 3-state result: explicit mismatch, confirmed match, or indeterminate (network/parse error). */
type VersionCheckResult = 'MISMATCH' | 'MATCH' | 'UNKNOWN';

/**
 * Checks whether the running app version (baked at build time)
 * matches the latest version served by the origin.  When a new
 * deploy lands on Cloudflare Pages the index.html contains the
 * new __APP_VERSION__ — fetching it and comparing tells us a
 * deploy happened even before the SW finishes its own diff.
 *
 * Returns UNKNOWN (instead of false) on any error so callers never
 * silently treat a network failure as "no update available".
 *
 * Uses a cache-busting param AND Cache-Control header because workbox
 * navigateFallback + globPatterns can intercept fetch('/?_vc=…') from
 * the SW cache; the explicit Cache-Control: no-cache header signals to
 * the SW network-first strategy to bypass the cache.
 */
async function checkVersionMismatch(): Promise<VersionCheckResult> {
  // Guard: if the build constant is absent (vite.config typo/removal),
  // disable version checks entirely to avoid an always-true toast loop.
  if (typeof __APP_VERSION__ !== 'string' || !__APP_VERSION__) {
    console.warn('[ReloadPrompt] __APP_VERSION__ missing — version check disabled');
    return 'UNKNOWN';
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), VERSION_CHECK_TIMEOUT_MS);

  try {
    const res = await fetch('/index.html?_vc=' + Date.now(), {
      cache: 'no-store',
      signal: controller.signal,
      headers: {
        'Cache-Control': 'no-cache',
        Accept: 'text/html',
      },
    });

    if (!res.ok) {
      // 5xx (e.g. Cloudflare mid-flight deploy) or unexpected status —
      // treat as indeterminate, not as "no mismatch".
      console.warn('[ReloadPrompt] version check got non-OK response:', res.status);
      return 'UNKNOWN';
    }

    const html = await res.text();
    // Vite injects __APP_VERSION__ as a string literal in the built JS.
    // The index.html contains a <script> tag referencing the entry chunk
    // which has the version baked in.  A simpler heuristic: look for a
    // <meta name="app-version"> tag we add to index.html.
    const match = html.match(/<meta\s+name="app-version"\s+content="([^"]+)"/);

    if (!match) {
      // Meta tag absent — plugin regression or mid-deploy partial response.
      console.warn('[ReloadPrompt] app-version meta tag absent in fetched HTML');
      Sentry.captureMessage('[ReloadPrompt] app-version meta tag missing in origin HTML', 'warning');
      return 'UNKNOWN';
    }

    return match[1] !== __APP_VERSION__ ? 'MISMATCH' : 'MATCH';
  } catch (err) {
    // Network failure, DNS error, AbortError (timeout), CSP block — all indeterminate.
    if (err instanceof Error && err.name !== 'AbortError') {
      console.warn('[ReloadPrompt] version check failed:', err.message);
      Sentry.captureException(err, { tags: { component: 'ReloadPrompt' } });
    }
    return 'UNKNOWN';
  } finally {
    clearTimeout(timeoutId);
  }
}

export function ReloadPrompt() {
  const { t } = useTranslation();

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        // Periodically ask the SW to check for updates
        setInterval(() => {
          registration.update().catch(console.error);
        }, SW_CHECK_INTERVAL);
      }
    },
    onRegisterError(error) {
      console.error('SW registration error:', error);
      Sentry.captureException(error, { tags: { component: 'ReloadPrompt', phase: 'sw-register' } });
    },
  });

  // Eagerly check version once on mount (after a short delay so the SW
  // registration doesn't race with this fetch).  This catches stale SW
  // caches that have been sitting in the browser since the last deploy —
  // users would otherwise wait up to SW_CHECK_INTERVAL before seeing the
  // update prompt.
  useEffect(() => {
    let cancelled = false;
    const timerId = setTimeout(async () => {
      const result = await checkVersionMismatch();
      // Only act on an explicit MISMATCH — UNKNOWN leaves prompt hidden (fail-safe).
      if (!cancelled && result === 'MISMATCH') setNeedRefresh(true);
    }, INITIAL_CHECK_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [setNeedRefresh]); // setNeedRefresh is stable (from useState tuple)

  // Fallback: version meta-tag check (catches deploys even if SW
  // update detection is slow or broken).
  useEffect(() => {
    const id = setInterval(async () => {
      if (needRefresh) return; // already showing prompt
      const result = await checkVersionMismatch();
      // Only act on an explicit MISMATCH — UNKNOWN leaves prompt hidden (fail-safe).
      if (result === 'MISMATCH') setNeedRefresh(true);
    }, SW_CHECK_INTERVAL);
    return () => clearInterval(id);
  }, [needRefresh, setNeedRefresh]);

  const handleUpdate = useCallback(async () => {
    // Force SW to fetch its own update before we reload, increasing odds
    // that the new SW is waiting and takes control after unregister.
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.update()));
    } catch {
      // Non-fatal — continue to updateServiceWorker regardless
    }

    try {
      await updateServiceWorker(true);
    } catch {
      // SW update failed — fall through to cache-purge + hard reload
    }

    // Unregister SW and purge all caches before reload; without this,
    // the browser may serve the same stale index.html from the SW cache.
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (err) {
      // Cache purge is best-effort — reload anyway so user isn't stuck.
      console.warn('[ReloadPrompt] cache purge failed', err);
    }

    window.location.reload();
  }, [updateServiceWorker]);

  const handleDismiss = useCallback(() => {
    setNeedRefresh(false);
  }, [setNeedRefresh]);

  return (
    <Snackbar
      open={needRefresh}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity="info"
        variant="filled"
        sx={{ width: '100%', alignItems: 'center' }}
        action={
          <>
            <Button color="inherit" size="small" onClick={handleDismiss}>
              {t('common.close')}
            </Button>
            <Button color="inherit" size="small" onClick={handleUpdate} sx={{ fontWeight: 700 }}>
              {t('pwa.updateNow')}
            </Button>
          </>
        }
      >
        {t('pwa.newVersionAvailable')}
      </Alert>
    </Snackbar>
  );
}
